import React, { useContext } from "react"
import { useWatch } from "react-hook-form"

import classNames from "classnames"

import { exists } from "shared/lib/exists"
import { getFormattingLocale } from "shared/lib/locale/formattingLocale"

import { InputContext } from "../../Input"
import { NumberInputContext } from "../NumberInput"

interface ReadonlyNumberProps
  extends Omit<
    React.PropsWithoutRef<React.HTMLProps<HTMLDivElement>>,
    "value"
  > {
  value?: number | string | null
  render?: (
    value: string | number | null,
    args: { raw: string | number | null }
  ) => React.JSX.Element | React.ReactNode
  format?: (value: string | number | null) => string | number | null
  showUnit?: boolean
}

export const ReadonlyNumber = React.memo((props: ReadonlyNumberProps) => {
  const { name } = useContext(InputContext)

  if (name) {
    return <HookFormReadonly {...props} />
  }

  return <Readonly {...props} />
})

export const HookFormReadonly = React.memo(
  ({ value: propsValue, ...props }: ReadonlyNumberProps) => {
    const { name } = useContext(InputContext)
    const value = useWatch({ name }) ?? ""

    return <Readonly {...props} value={propsValue ?? value} />
  }
)

export const Readonly = React.memo(
  ({
    className,
    children,
    render,
    value,
    showUnit = true,
    ...props
  }: ReadonlyNumberProps) => {
    const { readonly, loading, unit } = useContext(InputContext)
    const { format } = useContext(NumberInputContext)

    if (!readonly || loading) {
      return null
    }

    if (children) {
      return children as React.JSX.Element
    }

    if (!exists(value) || value === "") {
      return "-"
    }

    let formattedValue = "-"

    if (typeof value === "string") {
      formattedValue = value
    }
    if (typeof value === "number") {
      formattedValue = new Intl.NumberFormat(
        getFormattingLocale(),
        format
      ).format(value)
    }

    const shouldShowUnit = unit && showUnit
    const displayValue = shouldShowUnit
      ? `${formattedValue} ${unit}`
      : formattedValue

    let content: React.ReactNode | React.JSX.Element = displayValue

    if (render) {
      content = render(displayValue, { raw: value })
    }

    return (
      <div
        {...props}
        className={classNames(
          "input__value",
          "input__value--readonly",
          className
        )}
      >
        {content}
      </div>
    )
  }
)
